import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mic, Plus, Edit, Trash2, ExternalLink, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import RichTextEditor from '../../components/ui/rich-text-editor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPodcasts() {
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPodcast, setEditingPodcast] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    spotify_url: '',
    date: '',
    duration: '',
    episode_number: '',
    season_number: '',
    guest_speaker: '',
    is_visible: true
  });

  useEffect(() => {
    fetchPodcasts();
  }, []);

  const fetchPodcasts = async () => {
    try {
      const response = await axios.get(`${API}/podcasts`);
      setPodcasts(response.data);
    } catch (error) {
      toast.error('Failed to load podcasts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        episode_number: formData.episode_number ? parseInt(formData.episode_number) : null,
        season_number: formData.season_number ? parseInt(formData.season_number) : null
      };
      
      if (editingPodcast) {
        await axios.put(`${API}/podcasts/${editingPodcast.id}`, data);
        toast.success('Podcast updated');
      } else {
        await axios.post(`${API}/podcasts`, data);
        toast.success('Podcast created');
      }
      setShowDialog(false);
      resetForm();
      fetchPodcasts();
    } catch (error) {
      toast.error('Failed to save podcast');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this podcast?')) return;
    try {
      await axios.delete(`${API}/podcasts/${id}`);
      toast.success('Podcast deleted');
      fetchPodcasts();
    } catch (error) {
      toast.error('Failed to delete podcast');
    }
  };

  const openEdit = (podcast) => {
    setEditingPodcast(podcast);
    setFormData({
      title: podcast.title || '',
      description: podcast.description || '',
      spotify_url: podcast.spotify_url || '',
      date: podcast.date || '',
      duration: podcast.duration || '',
      episode_number: podcast.episode_number || '',
      season_number: podcast.season_number || '',
      guest_speaker: podcast.guest_speaker || '',
      is_visible: podcast.is_visible !== false
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingPodcast(null);
    setFormData({
      title: '',
      description: '',
      spotify_url: '',
      date: '',
      duration: '',
      episode_number: '',
      season_number: '',
      guest_speaker: '',
      is_visible: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-podcasts-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif text-charcoal">Podcasts</h1>
          <p className="text-charcoal/60">Manage podcast episodes</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowDialog(true); }}
          className="bg-burgundy hover:bg-purple text-white"
          data-testid="add-podcast-btn"
        >
          <Plus size={18} className="mr-2" /> Add Episode
        </Button>
      </div>

      <div className="grid gap-4">
        {podcasts.map((podcast) => (
          <motion.div
            key={podcast.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 border border-gold/10 hover:border-gold/30 transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mic size={24} className="text-purple" />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-charcoal">{podcast.title}</h3>
                  <div className="flex flex-wrap gap-3 text-sm text-charcoal/60 mt-1">
                    {podcast.season_number && <span>Season {podcast.season_number}</span>}
                    {podcast.episode_number && <span>Episode {podcast.episode_number}</span>}
                    <span>{podcast.date}</span>
                    {podcast.duration && <span>{podcast.duration}</span>}
                  </div>
                  {podcast.guest_speaker && (
                    <div className="flex items-center gap-1 text-sm text-gold mt-2">
                      <User size={14} /> Guest: {podcast.guest_speaker}
                    </div>
                  )}
                  {podcast.spotify_url && (
                    <a href={podcast.spotify_url} target="_blank" rel="noopener noreferrer" 
                       className="inline-flex items-center gap-1 text-sm text-[#1DB954] mt-2 hover:underline">
                      <ExternalLink size={14} /> Spotify
                    </a>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(podcast)} className="p-2 text-charcoal/50 hover:text-burgundy">
                  <Edit size={18} />
                </button>
                <button onClick={() => handleDelete(podcast.id)} className="p-2 text-charcoal/50 hover:text-red-500">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
        {podcasts.length === 0 && (
          <div className="text-center py-12 text-charcoal/50">
            <Mic size={48} className="mx-auto mb-4 opacity-20" />
            <p>No podcasts yet. Add your first episode!</p>
          </div>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editingPodcast ? 'Edit Podcast' : 'Add Podcast Episode'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Spotify URL *</Label>
              <Input
                value={formData.spotify_url}
                onChange={(e) => setFormData({...formData, spotify_url: e.target.value})}
                placeholder="https://open.spotify.com/episode/..."
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Duration</Label>
                <Input
                  value={formData.duration}
                  onChange={(e) => setFormData({...formData, duration: e.target.value})}
                  placeholder="32:15"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Season Number</Label>
                <Input
                  type="number"
                  value={formData.season_number}
                  onChange={(e) => setFormData({...formData, season_number: e.target.value})}
                />
              </div>
              <div>
                <Label>Episode Number</Label>
                <Input
                  type="number"
                  value={formData.episode_number}
                  onChange={(e) => setFormData({...formData, episode_number: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Guest Speaker</Label>
              <Input
                value={formData.guest_speaker}
                onChange={(e) => setFormData({...formData, guest_speaker: e.target.value})}
                placeholder="Guest name (if any)"
              />
            </div>
            <div>
              <Label>Description</Label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => setFormData({...formData, description: value})}
                placeholder="Episode description..."
                height="150px"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_visible"
                checked={formData.is_visible}
                onChange={(e) => setFormData({...formData, is_visible: e.target.checked})}
              />
              <Label htmlFor="is_visible">Visible on website</Label>
            </div>
            <Button type="submit" className="w-full bg-burgundy hover:bg-purple">
              {editingPodcast ? 'Update Podcast' : 'Create Podcast'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
